import { useCallback, useMemo } from 'react';
import {
  CustomerServiceTag,
  Dimensions,
  QuoteApiModel,
  QuoteOptionApiModel,
  QuoteProviderApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import {
  CurrentSelectionType,
  DefaultQuoteRequestType,
  ToSmartSendAnalyticsModel
} from '../quotes/utils/CurrentSelectionType';
import QuoteModal from '../quotes/QuoteModal';
import { GetTopCountry } from '../../../lib/helpers/GetTopCountry';
import { MarketplaceService } from '../../../lib/consts/ItemMetaConstants';

interface props {
  quoteProviders: () => Promise<QuoteProviderApiModel[]>;
  getQuotes: (
    request: DefaultQuoteRequestType,
    packageDimensions: Dimensions,
    quoteAmount: number,
    quoteSlug: string | null
  ) => Promise<QuoteApiModel[]>;
  confirm?: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  cancel?: () => void;
  affected: number;
  loggingSections: string[];
  selectedItems: () => SmartSendItemApiModel[];
  updateServiceTags: (add: CustomerServiceTag[], remove: CustomerServiceTag[]) => void;
  getServiceTags: () => Promise<CustomerServiceTag[]>;
}

export default function ApplyService({
  selectedItems,
  confirm,
  getQuotes,
  updateServiceTags,
  getServiceTags,
  quoteProviders,
  cancel,
  loggingSections
}: props) {
  const config = useMemo(() => {
    const items = selectedItems();
    const topCountry = GetTopCountry(items);
    const topDeliveryDetails = {
      Recipient: {
        ...topCountry.Recipient,
        Postcode: '',
        Provider: null,
        Name: null,
        Meta: null
      },
      Sender: {
        ...topCountry.Sender,
        Postcode: '',
        Provider: null,
        Name: null,
        Meta: null
      }
    };

    const packageDimensions: Dimensions =
      items.length > 0 &&
      items[0].PackageDimensions?.Weight > 0 &&
      items.every(
        (x) =>
          x.PackageDimensions.Weight === items[0].PackageDimensions.Weight &&
          x.PackageDimensions.Height === items[0].PackageDimensions.Height &&
          x.PackageDimensions.Width === items[0].PackageDimensions.Width &&
          x.PackageDimensions.Length === items[0].PackageDimensions.Length
      )
        ? items[0].PackageDimensions
        : { Weight: 1 };

    const quoteValue =
      items.length > 0 && items.every((x) => x.Value === items[0].Value) && items[0].Value > 0 ? items[0].Value : 20;

    const quoteSingularMarketplaceSlug =
      items.length > 0 &&
      items.every((x) =>
        x.MarketplaceOrderLines.every((m) => m.Source.Id === items[0].MarketplaceOrderLines[0].Source.Id)
      )
        ? items[0].MarketplaceOrderLines[0].Source.Id
        : null;

    const quoteSingularMarketplaceService =
      items.length > 0 &&
      items.every((x) => x.Meta?.[MarketplaceService]?.Value === items[0].Meta?.[MarketplaceService]?.Value)
        ? (items[0].Meta?.[MarketplaceService]?.Value ?? null)
        : null;

    const currentSelection: CurrentSelectionType = {
      limitToAccount: false,
      accountId: null,
      provider: null,
      id: null,
      extras: [],
      marketplaceService: quoteSingularMarketplaceService ?? undefined,
      sender: topDeliveryDetails.Sender,
      recipient: topDeliveryDetails.Recipient,
      items: items.map((item) => ToSmartSendAnalyticsModel(item))
    };

    return {
      currentSelection: currentSelection,
      recipient: topDeliveryDetails.Recipient,
      sender: topDeliveryDetails.Sender,
      packageDimensions: packageDimensions,
      quoteValue: quoteValue,
      quoteSingularMarketplaceSlug: quoteSingularMarketplaceSlug
    };
  }, [selectedItems]);

  const onServiceSaveCallback = useCallback(
    (quote: QuoteApiModel, option: QuoteOptionApiModel) => {
      confirm?.(quote, option);
    },
    [confirm]
  );

  const getQuotesCallback = useCallback(
    async (request: DefaultQuoteRequestType) =>
      getQuotes(request, config.packageDimensions, config.quoteValue, config.quoteSingularMarketplaceSlug),
    [getQuotes, config]
  );

  const getServiceTagsCallback = useCallback(() => getServiceTags(), [getServiceTags]);

  const updateServiceTagsCallback = useCallback(
    (add: CustomerServiceTag[], remove: CustomerServiceTag[]) => {
      updateServiceTags(add, remove);
    },
    [updateServiceTags]
  );

  const quoteProvidersCallback = useCallback(() => quoteProviders(), [quoteProviders]);

  if (!config) {
    return null;
  }

  return (
    <QuoteModal
      getServiceTags={getServiceTagsCallback}
      updateServiceTags={updateServiceTagsCallback}
      loggingSections={loggingSections}
      onSave={onServiceSaveCallback}
      close={cancel}
      currentSelection={config.currentSelection}
      getQuotes={getQuotesCallback}
      quoteProviders={quoteProvidersCallback}
    />
  );
}
